import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  business,
  careerOpenings,
  faqItems,
  heroSlides,
  locations,
  processSteps,
  products,
  reviews,
  serviceCards,
  serviceDetails,
  whyChooseUs,
} from "../data/jalRoData";
import { InquiryForm } from "./JalRoForms";
import { SectionTitle } from "./JalRoLayout";
import { useProducts, useServices } from "../hooks/useSiteData";

export const HeroSection = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((value) => (value + 1) % heroSlides.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, []);

  const slide = heroSlides[active];
  const previous = () => setActive((value) => (value === 0 ? heroSlides.length - 1 : value - 1));
  const next = () => setActive((value) => (value + 1) % heroSlides.length);

  return (
    <section className="jal-slider">
      {heroSlides.map((item, index) => (
        <div
          className={`jal-slide ${index === active ? "is-active" : ""}`}
          style={{ backgroundImage: `linear-gradient(105deg, rgba(5, 42, 72, 0.9), rgba(6, 152, 196, 0.58)), url(${item.image})` }}
          key={item.title}
        />
      ))}
      <div className="jal-slider-content">
        <div className="container">
          <div className="row align-items-center gy-5">
            <div className="col-lg-7">
              <div className="jal-hero-content">
                <span className="jal-eyebrow">RO Water Purifier Service & Repair</span>
                <h1>{slide.title}</h1>
                <p>{slide.subtitle}</p>
                <div className="jal-hero-actions">
                  <a className="jal-primary-btn" href={business.phoneHref}>
                    <i className="fas fa-phone-alt" /> Call Now
                  </a>
                  <a className="jal-secondary-btn" href={business.whatsapp}>
                    <i className="fab fa-whatsapp" /> WhatsApp
                  </a>
                </div>
                <div className="jal-hero-points">
                  <span>10+ Years Experience</span>
                  <span>Genuine Spare Parts</span>
                  <span>Same Day Service</span>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <InquiryForm compact source="Home Slider Enquiry" />
            </div>
          </div>
        </div>
      </div>
      <button className="jal-slider-arrow jal-slider-prev" type="button" onClick={previous} aria-label="Previous banner">
        <i className="fas fa-chevron-left" />
      </button>
      <button className="jal-slider-arrow jal-slider-next" type="button" onClick={next} aria-label="Next banner">
        <i className="fas fa-chevron-right" />
      </button>
      <div className="jal-slider-dots">
        {heroSlides.map((item, index) => (
          <button
            key={item.title}
            type="button"
            className={index === active ? "active" : ""}
            onClick={() => setActive(index)}
            aria-label={`Go to banner ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export const WhyChooseUsSection = () => (
  <section className="jal-section">
    <div className="container">
      <SectionTitle
        eyebrow="Why Choose Us"
        title="Trusted RO Service Experts"
        text="Fast support, transparent pricing, and genuine parts for domestic and commercial RO systems."
      />
      <div className="row g-4">
        {whyChooseUs.map((item) => (
          <div className="col-lg-4 col-md-6" key={item}>
            <div className="jal-feature-card">
              <i className="fas fa-check-circle" />
              <h3>{item}</h3>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ServicesSection = ({ showAll = false }) => (
  <ServicesSectionInner showAll={showAll} />
);

const ServicesSectionInner = ({ showAll }) => {
  const { items: runtimeServices } = useServices();
  const items = showAll ? runtimeServices : serviceCards;

  return (
    <section className="jal-section jal-section-soft">
      <div className="container">
        <SectionTitle
          eyebrow="Our Services"
          title="Complete RO Repair & Maintenance"
          text="Doorstep service for all major RO purifier brands across Delhi NCR."
        />
        <div className="row g-4">
          {items.map((service) => (
            <div className="col-lg-4 col-md-6" key={service.slug || service.title}>
              <div className="jal-service-card">
                <i className={service.icon} />
                <h3>{service.shortTitle || service.title}</h3>
                <p>{service.summary || service.text}</p>
                <Link to={`/services/${service.slug}`}>View Details</Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ProductsSection = () => {
  const { items: runtimeProducts } = useProducts();

  return (
    <section className="jal-section">
      <div className="container">
        <SectionTitle
          eyebrow="Featured Products"
          title="Domestic & Commercial RO Systems"
          text="Choose from compact home purifiers and high-capacity commercial RO plants."
        />
        <div className="row g-4">
          {runtimeProducts.map((product) => (
            <div className="col-lg-4 col-md-6" key={product.slug}>
              <div className="jal-product-card">
                <div className="jal-product-image">
                  <img src={product.image} alt={product.title} />
                </div>
                <div className="jal-product-body">
                  <h3>{product.title}</h3>
                  {product.price && <div className="jal-price">{product.price}</div>}
                  <p>{product.text}</p>
                  <div className="jal-card-actions">
                    <Link to={`/products/${product.slug}`}>View Details</Link>
                    <a href={business.whatsapp}>Get Best Price</a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ProductDetailSection = ({ product }) => {
  const activeProduct = product || products[0];

  return (
    <section className="jal-section">
      <div className="container">
        <div className="row gy-4 align-items-start">
          <div className="col-lg-6">
            <div className="jal-detail-gallery">
              <img src={activeProduct.image} alt={activeProduct.title} />
              <div className="jal-mini-gallery">
                {(activeProduct.gallery || [activeProduct.image]).map((image) => (
                  <img key={image} src={image} alt={activeProduct.title} />
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="jal-detail-copy">
              <span>Product Details</span>
              <h2>{activeProduct.title}</h2>
              {activeProduct.price && <div className="jal-detail-price">{activeProduct.price}</div>}
              <p>{activeProduct.overview}</p>
              <div className="jal-hero-actions">
                <a className="jal-primary-btn" href={business.phoneHref}>
                  <i className="fas fa-phone-alt" /> Call for Price
                </a>
                <a className="jal-secondary-btn" href={business.whatsapp}>
                  <i className="fab fa-whatsapp" /> Enquire on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="row g-4 mt-4">
          <div className="col-lg-7">
            <div className="jal-info-box">
              <h3>Key Features</h3>
              <ul className="jal-check-list">
                {activeProduct.features.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="jal-info-box">
              <h3>Specifications</h3>
              <div className="jal-spec-table">
                {activeProduct.specifications.map(([label, value]) => (
                  <div key={label}>
                    <strong>{label}</strong>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="jal-info-box">
              <h3>Best For</h3>
              <div className="jal-tag-grid justify-content-start">
                {activeProduct.applications.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const ServiceDetailSection = ({ service }) => {
  const activeService = service || serviceDetails[0];

  return (
    <section className="jal-section">
      <div className="container">
        <div className="row gy-4 align-items-center">
          <div className="col-lg-6">
            <div className="jal-detail-copy">
              <span>Doorstep RO Service</span>
              <h2>{activeService.title}</h2>
              <p>{activeService.details}</p>
              <div className="jal-hero-actions">
                <a className="jal-primary-btn" href={business.phoneHref}>
                  <i className="fas fa-phone-alt" /> Book Service
                </a>
                <a className="jal-secondary-btn" href={business.whatsapp}>
                  <i className="fab fa-whatsapp" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="jal-detail-image">
              <img src={activeService.image} alt={activeService.title} />
            </div>
          </div>
        </div>
        <div className="row g-4 mt-4">
          <div className="col-lg-6">
            <div className="jal-info-box">
              <h3>Common Problems We Fix</h3>
              <ul className="jal-check-list">
                {activeService.problems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="jal-info-box">
              <h3>Service Includes</h3>
              <ul className="jal-check-list">
                {activeService.includes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-12">
            <div className="jal-info-box">
              <h3>Frequently Asked Questions</h3>
              <div className="jal-faq-list">
                {activeService.faqs.map(([question, answer]) => (
                  <details key={question} open>
                    <summary>{question}</summary>
                    <p>{answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const ProcessSection = () => (
  <section className="jal-section jal-section-dark">
    <div className="container">
      <SectionTitle
        eyebrow="Service Process"
        title="Simple, Fast, Transparent"
        text="From booking to final water quality check, every step is clear."
        light
      />
      <div className="row g-4">
        {processSteps.map((step, index) => (
          <div className="col-lg-3 col-md-6" key={step.title}>
            <div className="jal-process-card">
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ReviewsSection = () => (
  <section className="jal-section">
    <div className="container">
      <SectionTitle eyebrow="Customer Reviews" title="Reliable Service, Happy Customers" />
      <div className="row g-4">
        {reviews.map((review) => (
          <div className="col-lg-4" key={review.name}>
            <div className="jal-review-card">
              <div className="jal-stars">5/5</div>
              <p>"{review.text}"</p>
              <h3>{review.name}</h3>
              <span>{review.location}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ServiceAreasSection = ({ detailed = false }) => (
  <section className="jal-section jal-section-soft">
    <div className="container">
      <SectionTitle
        eyebrow="Service Areas"
        title="RO Service Across Delhi NCR"
        text="Quick doorstep support in Delhi, Noida, Greater Noida, Ghaziabad, Faridabad, and Gurugram."
      />
      <div className="jal-location-grid">
        {locations.map((location) => (
          <div className="jal-location-card" key={location}>
            <i className="fas fa-location-dot" />
            <h3>{location}</h3>
            <p>RO installation, repair, AMC, filter change and water purifier maintenance.</p>
            <Link to={`/service-areas#${location.toLowerCase().replace(/\s+/g, "-")}`}>Get Service</Link>
          </div>
        ))}
      </div>
      {detailed && (
        <div className="jal-area-copy">
          <p>
            Our technicians cover residential societies, offices, shops, restaurants, schools and commercial spaces
            across Delhi NCR. Call now for same day RO repair and maintenance support near you.
          </p>
        </div>
      )}
    </div>
  </section>
);

export const ContactCtaSection = () => (
  <section className="jal-cta">
    <div className="container">
      <div className="jal-cta-inner">
        <div>
          <span>Need RO service today?</span>
          <h2>Call Jal RO System for fast doorstep support.</h2>
        </div>
        <div className="jal-hero-actions">
          <a className="jal-primary-btn" href={business.phoneHref}>
            <i className="fas fa-phone-alt" /> {business.phone}
          </a>
          <a className="jal-secondary-btn" href={business.whatsapp}>
            <i className="fab fa-whatsapp" /> WhatsApp
          </a>
        </div>
      </div>
    </div>
  </section>
);

export const AboutContentSection = () => (
  <section className="jal-section">
    <div className="container">
      <div className="row align-items-center gy-4">
        <div className="col-lg-6">
          <SectionTitle
            eyebrow="About Jal RO System"
            title="10+ Years of RO Water Purifier Service Experience"
            text="We help homes and businesses get clean drinking water with dependable RO repair, installation, AMC and maintenance services."
          />
          <p className="jal-about-text">
            Jal RO System serves Delhi NCR with trained technicians, genuine spare parts, affordable pricing and same
            day doorstep support. From domestic purifier repair to commercial RO plant maintenance, our focus is clean
            water, clear communication and customer satisfaction.
          </p>
        </div>
        <div className="col-lg-6">
          <div className="jal-about-image">
            <img src="/assets/img/jal-ro/service-tech.png" alt="Jal RO System technician" />
          </div>
        </div>
        <div className="col-12">
          <div className="jal-about-panel">
            {whyChooseUs.map((item) => (
              <div key={item}>
                <i className="fas fa-circle-check" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const FaqSection = () => (
  <section className="jal-section">
    <div className="container">
      <SectionTitle
        eyebrow="FAQ"
        title="RO Service Questions"
        text="Quick answers about RO repair, AMC, spare parts, service areas and enquiry process."
      />
      <div className="jal-faq-list">
        {faqItems.map(([question, answer]) => (
          <details key={question}>
            <summary>{question}</summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

export const CareersSection = () => (
  <section className="jal-section">
    <div className="container">
      <SectionTitle
        eyebrow="Careers"
        title="Work With Jal RO System"
        text="We are looking for service-focused people who can help customers get clean water with confidence."
      />
      <div className="row g-4">
        {careerOpenings.map((job) => (
          <div className="col-lg-4 col-md-6" key={job.title}>
            <div className="jal-career-card">
              <span>{job.type}</span>
              <h3>{job.title}</h3>
              <p>{job.text}</p>
              <small>{job.location}</small>
              <Link to="/contact">Apply Now</Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const ContactSection = () => (
  <section className="jal-section">
    <div className="container">
      <div className="row gy-4">
        <div className="col-lg-5">
          <SectionTitle eyebrow="Contact Us" title="Book Your RO Service" />
          <div className="jal-contact-card">
            <p>
              <i className="fas fa-phone-alt" />
              <a href={business.phoneHref}>{business.phone}</a>
            </p>
            <p>
              <i className="fab fa-whatsapp" />
              <a href={business.whatsapp}>Chat on WhatsApp</a>
            </p>
            <p>
              <i className="fas fa-envelope" />
              <a href={business.emailHref}>{business.email}</a>
            </p>
            <p>
              <i className="fas fa-map-marker-alt" />
              <span>{business.address}</span>
            </p>
          </div>
          <iframe
            className="jal-map"
            title="Jal RO System Delhi NCR Map"
            loading="lazy"
            src="https://www.google.com/maps?q=Delhi%20NCR%20India&output=embed"
          />
        </div>
        <div className="col-lg-7">
          <InquiryForm source="Contact Page Enquiry" />
        </div>
      </div>
    </div>
  </section>
);

export const RelatedLinksSection = ({ currentSlug, type = "service" }) => {
  const { items: runtimeProducts } = useProducts();
  const { items: runtimeServices } = useServices();
  const items = useMemo(() => {
    const source = type === "product" ? runtimeProducts : runtimeServices;
    return source.filter((item) => item.slug !== currentSlug).slice(0, 6);
  }, [currentSlug, runtimeProducts, runtimeServices, type]);

  return (
    <section className="jal-section jal-section-soft">
      <div className="container">
        <SectionTitle eyebrow={type === "product" ? "More Products" : "More Services"} title="Explore More" />
        <div className="row g-4">
          {items.map((item) => (
            <div className="col-lg-4 col-md-6" key={item.slug}>
              <Link className="jal-related-card" to={`/${type === "product" ? "products" : "services"}/${item.slug}`}>
                <img src={item.image} alt={item.title} />
                <span>{item.shortTitle || item.title}</span>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const PageHero = ({ title, text, image = "/assets/img/jal-ro/palak-slide-1.jpg" }) => (
  <section className="jal-page-hero" style={{ backgroundImage: `linear-gradient(105deg, rgba(5, 42, 72, 0.92), rgba(6, 152, 196, 0.64)), url(${image})` }}>
    <div className="container">
      <span>Jal RO System</span>
      <h1>{title}</h1>
      {text && <p>{text}</p>}
    </div>
  </section>
);
